import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Check, AlertCircle } from 'lucide-react';
import notificationService from '../services/notificationService';

const NotificationPermission = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    // Check notification permission status
    const checkPermission = () => {
      const status = notificationService.getPermissionStatus();
      setPermission(status);

      // Show prompt if we should ask for permission
      if (status === 'default' && notificationService.shouldAskPermission()) {
        // Delay showing prompt to avoid being too aggressive
        setTimeout(() => {
          setShowPrompt(true);
        }, 5000);
      }
    };

    checkPermission();

    // Initialize notification service
    notificationService.initialize();
  }, []);

  const handleEnableNotifications = async () => {
    setIsRequesting(true);
    
    try {
      const result = await notificationService.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        // Show success notification
        notificationService.showTypedNotification(
          'success',
          'Notifications Enabled!',
          'You will now receive real-time notifications from Lockbox.',
          {
            vibrate: [200, 100, 200]
          }
        );
      }
      
      // Hide prompt regardless of result
      setShowPrompt(false);
    } catch (error) {
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Store dismissal to avoid showing again too soon
    localStorage.setItem('notification_prompt_dismissed', Date.now().toString());
  };

  // Don't render if notifications aren't supported
  if (!notificationService.isSupported()) {
    return null;
  }

  return (
    <>
      {/* Floating Notification Prompt */}
      <AnimatePresence>
        {showPrompt && permission === 'default' && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="fixed bottom-4 right-4 z-50 max-w-sm"
          >
            <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Bell className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">
                      Enable Notifications
                    </h3>
                    <p className="text-xs text-gray-600 mb-3">
                      Get real-time updates about your datarooms, invitations, and important activities.
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleEnableNotifications}
                        disabled={isRequesting}
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isRequesting ? 'Requesting...' : 'Enable Notifications'}
                      </button>
                      <button
                        onClick={handleDismiss}
                        className="px-3 py-1.5 text-gray-600 text-xs font-medium hover:text-gray-800 transition-colors"
                      >
                        Not Now
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={handleDismiss}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
};

export default NotificationPermission;